import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, TrendingUp, Activity, Clock, ShieldCheck } from 'lucide-react';
import { Reveal } from './ui/Reveal';

const features = [
    'Cut operational costs by up to 60%',
    'Eliminate manual data-entry errors',
    '24/7 monitoring with instant alerts',
    'Integrates with 5,000+ apps & APIs',
    'Full documentation & team training',
    'Architecture that scales as you grow',
];

const miniStats = [
    { icon: Clock, label: 'Hours saved / week', value: '38h', tint: 'text-brand-cyan' },
    { icon: TrendingUp, label: 'Tasks automated', value: '1,240', tint: 'text-emerald-400' },
    { icon: ShieldCheck, label: 'Error rate', value: '0.0%', tint: 'text-brand-orange' },
];

const bars = [42, 65, 48, 80, 58, 92, 74];

const Features: React.FC = () => {
    return (
        <section className="relative py-24 md:py-32 bg-navy-800/40 border-y border-white/[0.05] overflow-hidden">
            <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-brand-blue/10 rounded-full blur-[130px] pointer-events-none" />

            <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                {/* Copy */}
                <div>
                    <span className="eyebrow mb-4">
                        <span className="h-px w-6 bg-brand-cyan/60" />
                        Why AutomateIT.ng
                    </span>
                    <h2 className="font-display text-3xl md:text-[2.75rem] md:leading-[1.1] font-bold text-white tracking-tight text-balance">
                        Engineering-grade automation, <span className="text-gradient">built to last</span>
                    </h2>
                    <p className="mt-5 text-lg text-text-muted leading-relaxed max-w-xl">
                        We pair deep technical expertise with real business sense to build systems that
                        actually move the needle — no bloat, no brittle no-code spaghetti. Just reliable
                        automation you can trust in production.
                    </p>

                    <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                        {features.map((feature, i) => (
                            <Reveal key={feature} delay={i * 0.05} y={12} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-brand-cyan shrink-0 mt-0.5" />
                                <span className="text-text-main">{feature}</span>
                            </Reveal>
                        ))}
                    </div>
                </div>

                {/* Live dashboard visual */}
                <Reveal className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-tr from-brand-cyan/15 to-brand-blue/10 rounded-3xl blur-2xl opacity-60 pointer-events-none" />
                    <div className="relative rounded-2xl glass shadow-lift p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="text-sm text-text-muted">Automation control center</div>
                                <div className="font-display text-lg font-bold text-white">This week</div>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full">
                                <Activity className="w-3.5 h-3.5" /> All systems go
                            </span>
                        </div>

                        {/* mini stats */}
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            {miniStats.map((s) => (
                                <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/[0.05] p-3">
                                    <s.icon className={`w-4 h-4 ${s.tint} mb-2`} />
                                    <div className="font-display text-xl font-bold text-white tabular-nums">{s.value}</div>
                                    <div className="text-[11px] text-text-faint leading-tight mt-0.5">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* animated bar chart */}
                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4">
                            <div className="flex items-end justify-between gap-2 h-28">
                                {bars.map((h, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ height: 0 }}
                                        whileInView={{ height: `${h}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                        className="flex-1 rounded-t-md bg-gradient-to-t from-brand-blue to-brand-cyan"
                                    />
                                ))}
                            </div>
                            <div className="flex justify-between mt-3 text-[10px] font-mono text-text-faint">
                                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                                    <span key={i} className="flex-1 text-center">{d}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Features;
